import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Popconfirm, Progress, Spin, message } from "antd";
import { DownloadOutlined, SyncOutlined } from "@ant-design/icons";
import QRCode from "react-qr-code";
import axios from "axios";
import "./WireGuardInfo.css";
import {
  downloadWireGuardArchive,
  downloadWireGuardConfig,
  getWireGuardConfig,
  getWireGuardServers,
  provisionWireGuardProfile,
  WIREGUARD_PROVISION_POLL_DELAYS_MS,
  type VpnJob,
  type WireGuardServer,
} from "./api_v2/vpn";

type Props = { user: { id?: number } | null };
type Platform = "android" | "ios" | null;

type ServerState = Record<string, string | null>;
type QueueState = Record<string, string | null>;
type BundleServerStatus = "ready" | "preparing" | "failed";
type BundleStatusState = Record<string, BundleServerStatus>;

const SERVER_CODE_LABELS: Record<string, string> = {
  vk1: "Mobile1",
  de3: "Mobile2",
  de1: "WiFi1",
  de2: "WiFi2",
  us1: "AiGemini",
  eu1: "ReservWiFi",
};

const SERVER_CODE_DOWNLOAD_NAMES: Record<string, string> = {
  vk1: "Mobile1",
  de3: "Mobile2",
  de1: "WiFi1",
  de2: "WiFi2",
  us1: "AiGemini",
  eu1: "ReservWiFi",
};

const SERVER_ORDER = [
  "wg-vk1.wgsimol.ru:51820",
  "90.156.218.112:51820",
  "185.130.115.161:53",
  "185.130.115.161:51820",
  "195.54.32.165:51194",
  "195.54.32.51:51820",
  "142.252.220.24:51820",
  "91.239.23.23:51820",
] as const;

const SERVER_LABELS: Record<string, string> = {
  "wg-vk1.wgsimol.ru:51820": "Mobile1",
  "90.156.218.112:51820": "Mobile1",
  "185.130.115.161:53": "Mobile2",
  "185.130.115.161:51820": "Mobile2",
  "195.54.32.51:51820": "WiFi2",
  "142.252.220.24:51820": "AiGemini",
  "91.239.23.23:51820": "ReservWiFi",
  "195.54.32.165:51194": "WiFi1",
};

const SERVER_DOWNLOAD_NAMES: Record<string, string> = {
  "wg-vk1.wgsimol.ru:51820": "Mobile1",
  "90.156.218.112:51820": "Mobile1",
  "185.130.115.161:53": "Mobile2",
  "185.130.115.161:51820": "Mobile2",
  "195.54.32.51:51820": "WiFi2",
  "142.252.220.24:51820": "AiGemini",
  "91.239.23.23:51820": "ReservWiFi",
  "195.54.32.165:51194": "WiFi1",
};

function normalizeServers(items: WireGuardServer[]): WireGuardServer[] {
  const rank = new Map<string, number>(SERVER_ORDER.map((endpoint, index) => [endpoint, index]));

  return [...items]
    .sort((a, b) => {
      const aRank = rank.get(a.endpoint) ?? Number.MAX_SAFE_INTEGER;
      const bRank = rank.get(b.endpoint) ?? Number.MAX_SAFE_INTEGER;
      if (aRank !== bRank) {
        return aRank - bRank;
      }
      return a.endpoint.localeCompare(b.endpoint);
    })
    .map((server) => ({
      ...server,
      name: SERVER_CODE_LABELS[server.code] ?? SERVER_LABELS[server.endpoint] ?? server.name,
    }));
}

function getDownloadFileName(server: WireGuardServer): string {
  const safeName =
    SERVER_CODE_DOWNLOAD_NAMES[server.code] ??
    SERVER_DOWNLOAD_NAMES[server.endpoint] ??
    server.code.replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
  return `${safeName}.conf`;
}

function describeJob(job: VpnJob, pollNumber: number): string {
  if (job.status === "processing") {
    return `Профиль создаётся на сервере. Проверка ${pollNumber + 1}.`;
  }
  if (job.status === "done") return "Профиль готов, загружаем конфигурацию…";
  if (job.status === "error") return "Не удалось создать профиль.";
  return `Профиль поставлен в очередь. Проверка ${pollNumber + 1}.`;
}

function isCancelled(error: unknown): boolean {
  return axios.isCancel(error) || (error instanceof DOMException && error.name === "AbortError");
}

function isValidWireGuardConfig(config: string | null | undefined): config is string {
  return typeof config === "string"
    && config.trimStart().startsWith("[Interface]")
    && config.length <= 32768;
}

const WireGuardInfo: React.FC<Props> = () => {
  const [servers, setServers] = useState<WireGuardServer[]>([]);
  const [configs, setConfigs] = useState<ServerState>({});
  const [loading, setLoading] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleStatuses, setBundleStatuses] = useState<BundleStatusState>({});
  const bundleInFlightRef = useRef(false);
  const [platform, setPlatform] = useState<Platform>(null);
  const [selectedServerCode, setSelectedServerCode] = useState<string | null>(null);
  const [queueState, setQueueState] = useState<QueueState>({});
  const loadControllerRef = useRef<AbortController | null>(null);
  const operationControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || (window as Window & { opera?: string }).opera || "";
    if (/android/i.test(ua)) setPlatform("android");
    else if (/iPad|iPhone|iPod/.test(ua)) setPlatform("ios");
  }, []);

  const loadServers = useCallback(async () => {
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;
    try {
      setLoading(true);
      const items = normalizeServers(await getWireGuardServers(controller.signal));

      setServers(items);
      setSelectedServerCode((current) => current || items[0]?.code || null);

      const loaded = await Promise.all(
        items.map(async (server) => {
          try {
            const config = await getWireGuardConfig(server.code, controller.signal);
            return [server.code, config] as const;
          } catch (error) {
            if (isCancelled(error)) throw error;
            if (axios.isAxiosError(error) && error.response?.status !== 404) {
              console.error(error);
            }
            return [server.code, null] as const;
          }
        })
      );

      setConfigs(Object.fromEntries(loaded));
    } catch (error) {
      if (isCancelled(error)) return;
      console.error(error);
      if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
        message.warning("Сервер ещё завершает создание профиля. Подождите минуту и обновите страницу — не нажимайте повторно.");
      } else if (axios.isAxiosError(error) && error.response?.status === 401) {
        message.error("Для WireGuard нужен вход через новый v2-токен. Текущая сессия WireGuard не авторизована.");
      } else if (axios.isAxiosError(error) && error.response?.status === 429) {
        message.error("WireGuard временно ограничил частые запросы. Подождите немного и обновите страницу.");
      } else {
        message.error("Не удалось загрузить список серверов WireGuard");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServers();
    return () => loadControllerRef.current?.abort();
  }, [loadServers]);

  useEffect(() => () => operationControllerRef.current?.abort(), []);

  const generate = async (server: string) => {
    operationControllerRef.current?.abort();
    const controller = new AbortController();
    operationControllerRef.current = controller;
    try {
      setLoading(true);
      setQueueState((previous) => ({ ...previous, [server]: "Отправляем запрос на создание профиля…" }));
      const result = await provisionWireGuardProfile(server, "generate", {
        signal: controller.signal,
        onStatus: (job, pollNumber) => {
          if (!controller.signal.aborted) {
            setQueueState((previous) => ({ ...previous, [server]: describeJob(job, pollNumber) }));
          }
        },
      });
      if (result.state === "pending") {
        setQueueState((previous) => ({
          ...previous,
          [server]: "Профиль остаётся в очереди. Можно закрыть страницу и повторить проверку позже.",
        }));
        message.info("Создание профиля продолжается в фоне");
        return;
      }

      setConfigs((previous) => ({ ...previous, [server]: result.config }));
      setQueueState((previous) => ({ ...previous, [server]: null }));
      message.success("Профиль готов");
    } catch (error) {
      if (isCancelled(error)) return;
      setQueueState((previous) => ({ ...previous, [server]: "Создать профиль сейчас не удалось." }));
      console.error(error);
      if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
        message.warning("Сервер ещё завершает обновление. Подождите минуту и обновите страницу — повторно нажимать не нужно.");
      } else if (axios.isAxiosError(error) && error.response?.status === 401) {
        message.error("Сессия WireGuard не авторизована");
      } else if (axios.isAxiosError(error) && error.response?.status === 502) {
        message.error("Сервер WireGuard временно недоступен. Попробуйте чуть позже.");
      } else if (axios.isAxiosError(error) && error.response?.status === 429) {
        message.error("Слишком много попыток генерации профиля. Попробуйте позже.");
      } else {
        message.error("Ошибка сети");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const refresh = async (server: string) => {
    operationControllerRef.current?.abort();
    const controller = new AbortController();
    operationControllerRef.current = controller;
    try {
      setLoading(true);
      setQueueState((previous) => ({ ...previous, [server]: "Отправляем запрос на обновление профиля…" }));
      const result = await provisionWireGuardProfile(server, "refresh", {
        signal: controller.signal,
        onStatus: (job, pollNumber) => {
          if (!controller.signal.aborted) {
            setQueueState((previous) => ({ ...previous, [server]: describeJob(job, pollNumber) }));
          }
        },
      });
      if (result.state === "pending") {
        setQueueState((previous) => ({
          ...previous,
          [server]: "Обновление остаётся в очереди. Старый профиль пока отображается; проверьте статус позже.",
        }));
        message.info("Обновление профиля продолжается в фоне");
        return;
      }

      setConfigs((previous) => ({ ...previous, [server]: result.config }));
      setQueueState((previous) => ({ ...previous, [server]: null }));
      message.success("Профиль обновлен. Старый профиль отключен");
    } catch (error) {
      if (isCancelled(error)) return;
      setQueueState((previous) => ({ ...previous, [server]: "Обновить профиль сейчас не удалось." }));
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        message.error("Сессия WireGuard не авторизована");
      } else if (axios.isAxiosError(error) && error.response?.status === 502) {
        message.error("Сервер WireGuard временно недоступен. Попробуйте чуть позже");
      } else if (axios.isAxiosError(error) && error.response?.status === 429) {
        message.error("Слишком много обновлений профиля. Попробуйте позже");
      } else {
        message.error("Не удалось обновить профиль");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const download = async (server: WireGuardServer) => {
    operationControllerRef.current?.abort();
    const controller = new AbortController();
    operationControllerRef.current = controller;
    try {
      setLoading(true);
      const blob = await downloadWireGuardConfig(server.code, controller.signal);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getDownloadFileName(server);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Safari/iOS may start reading the Blob only after the click handler
      // returns. Revoking immediately makes a valid download look like a
      // button that did nothing.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (error) {
      if (isCancelled(error)) return;
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        message.error("Сессия WireGuard не авторизована");
      } else if (axios.isAxiosError(error) && error.response?.status === 502) {
        message.error("Сервер WireGuard временно недоступен. Попробуйте чуть позже.");
      } else if (axios.isAxiosError(error) && error.response?.status === 429) {
        message.error("WireGuard временно ограничил частые запросы. Попробуйте позже.");
      } else {
        message.error("Не удалось скачать профиль");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const downloadAll = async () => {
    // React state updates after the event handler returns. The ref closes the
    // small window in which a fast double tap on iOS could start two archives.
    if (bundleInFlightRef.current) return;
    if (servers.length === 0) {
      message.warning("Список серверов WireGuard пока не загружен");
      return;
    }

    bundleInFlightRef.current = true;
    operationControllerRef.current?.abort();
    const controller = new AbortController();
    operationControllerRef.current = controller;
    setBundleLoading(true);
    setBundleStatuses(Object.fromEntries(
      servers.map((server) => [
        server.code,
        isValidWireGuardConfig(configs[server.code]) ? "ready" : "preparing",
      ])
    ));
    try {
      // Read every server concurrently. One unavailable node can now delay the
      // bundle by at most the API timeout instead of blocking all later nodes.
      // Existing profiles are never regenerated here. Missing profiles are
      // enqueued once and polled for a bounded time; the durable worker may
      // finish the rest in the background without freezing this page.
      let snapshots = await Promise.all(servers.map(async (server) => {
        try {
          const fresh = await getWireGuardConfig(server.code, controller.signal);
          if (isValidWireGuardConfig(fresh)) {
            setBundleStatuses((previous) => ({ ...previous, [server.code]: "ready" }));
          }
          return { server, config: isValidWireGuardConfig(fresh) ? fresh : configs[server.code] };
        } catch (error) {
          if (isCancelled(error)) throw error;
          console.error(`WireGuard bundle read: ${server.code}`, error);
          return { server, config: configs[server.code] };
        }
      }));

      const missingServers = snapshots.filter((item) => !isValidWireGuardConfig(item.config));
      if (missingServers.length > 0) {
        message.info(`Готовим недостающие профили: ${missingServers.length}`);
        const prepared = await Promise.all(missingServers.map(async (item) => {
          try {
            setQueueState((previous) => ({
              ...previous,
              [item.server.code]: "Ставим недостающий профиль в очередь…",
            }));
            const result = await provisionWireGuardProfile(item.server.code, "generate", {
              signal: controller.signal,
              pollDelaysMs: WIREGUARD_PROVISION_POLL_DELAYS_MS,
              onStatus: (job, pollNumber) => {
                if (!controller.signal.aborted) {
                  setQueueState((previous) => ({
                    ...previous,
                    [item.server.code]: describeJob(job, pollNumber),
                  }));
                }
              },
            });
            const ready = result.state === "ready" && isValidWireGuardConfig(result.config);
            setBundleStatuses((previous) => ({
              ...previous,
              [item.server.code]: ready ? "ready" : "failed",
            }));
            setQueueState((previous) => ({
              ...previous,
              [item.server.code]: ready ? null : "Профиль продолжает создаваться в фоне.",
            }));
            return {
              server: item.server,
              config: ready ? result.config : null,
            };
          } catch (error) {
            if (isCancelled(error)) throw error;
            console.error(`WireGuard bundle provision: ${item.server.code}`, error);
            setBundleStatuses((previous) => ({ ...previous, [item.server.code]: "failed" }));
            setQueueState((previous) => ({
              ...previous,
              [item.server.code]: "Создать профиль сейчас не удалось.",
            }));
            return item;
          }
        }));
        const preparedByCode = new Map(prepared.map((item) => [item.server.code, item]));
        snapshots = snapshots.map((item) => preparedByCode.get(item.server.code) ?? item);
      }

      const nextConfigs = { ...configs };
      const ready = snapshots.filter((item) => isValidWireGuardConfig(item.config));
      const failed = snapshots.filter((item) => !isValidWireGuardConfig(item.config));
      for (const item of ready) nextConfigs[item.server.code] = item.config as string;
      setConfigs(nextConfigs);

      if (ready.length === 0) {
        message.error("Не удалось подготовить ни одного профиля для ZIP-архива");
        return;
      }
      if (failed.length > 0) {
        message.warning(
          `ZIP будет собран из готовых профилей. Не вошли: ${failed.map((item) => item.server.name).join(", ")}`
        );
      }

      const blob = await downloadWireGuardArchive(controller.signal);
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Сервер вернул пустой ZIP-архив");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "SimolApps-WireGuard-profiles.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
      message.success(
        failed.length > 0
          ? `Скачаны доступные профили: ${ready.length}`
          : `Скачаны все профили: ${ready.length}`
      );
    } catch (error) {
      if (isCancelled(error)) return;
      console.error("WireGuard bundle", error);
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        const retryAfter = Number(error.response.headers?.["retry-after"] || 0);
        message.error(retryAfter > 0
          ? `Слишком много скачиваний. Повторите через ${retryAfter} сек.`
          : "Слишком много скачиваний. Повторите через минуту.");
      } else if (axios.isAxiosError(error) && error.response?.status === 401) {
        message.error("Сессия истекла. Войдите снова.");
      } else {
        message.error("Не удалось создать ZIP с конфигурациями");
      }
    } finally {
      bundleInFlightRef.current = false;
      setBundleLoading(false);
    }
  };

  const apps = [
    { key: "ios-download", platform: "ios", label: "Apple архив", icon: "/apple.svg", iconAlt: "Apple", href: "https://simolapps.ru/instal/wireguard_1_0_16.zip" },
    { key: "ios-store", platform: "ios", label: "Apple App Store", icon: "/apple.svg", iconAlt: "Apple", href: "https://apps.apple.com/app/wireguard/id1441195209" },
    { key: "android-apk", platform: "android", label: "Android APK", icon: "/Android.svg", iconAlt: "Android", href: "https://simolapps.ru/instal/com.wireguard.android-1.0.20260315.apk", download: "WireGuard-Android.apk" },
    { key: "android-store", platform: "android", label: "Android Google Play", icon: "/Android.svg", iconAlt: "Android", href: "https://play.google.com/store/apps/details?id=com.wireguard.android" },
    { key: "windows", platform: "windows", label: "Windows EXE", icon: "/win.svg", iconAlt: "Windows", href: "https://simolapps.ru/instal/wireguard-installer.exe" },
  ];
  const selectedServer =
    servers.find((server) => server.code === selectedServerCode) || servers[0] || null;
  const selectedConfig = selectedServer ? configs[selectedServer.code] ?? null : null;
  const selectedQueueMessage = selectedServer ? queueState[selectedServer.code] ?? null : null;
  const bundleReadyCount = servers.filter((server) => bundleStatuses[server.code] === "ready").length;
  const bundlePercent = servers.length > 0 ? Math.round((bundleReadyCount / servers.length) * 100) : 0;

  return (
    <div className="wireguard-page">
      <section className="wireguard-step-grid">
        <div className="wireguard-step-card">
          <p className="wireguard-step-card__index">1. Получите профиль</p>
          <p className="wireguard-step-card__subtitle">
            Выберите сервер, затем сканируйте QR-код или скачайте `.conf` файл.
          </p>

          <Card className="wireguard-config-card" style={{ overflow: "hidden" }}>
            {loading && servers.length === 0 ? (
              <div className="wireguard-config-card__loading">
                <Spin size="large" />
              </div>
            ) : servers.length === 0 ? (
              <div className="wireguard-config-card__empty">
                <p>Список серверов WireGuard пока не загружен.</p>
                <p className="wireguard-config-card__hint">
                  Обычно это значит, что backend ещё не отдал ни одного сервера или требуется повторный вход.
                </p>
                <div className="wireguard-config-card__actions">
                  <Button type="primary" onClick={loadServers}>
                    Обновить список
                  </Button>
                </div>

              </div>
            ) : (
              <div className="wireguard-server-panel">
                <div className="wireguard-server-picker" aria-label="Выбор сервера WireGuard">
                  {servers.map((server) => (
                    <button
                      key={server.code}
                      type="button"
                      className={`wireguard-server-option ${
                        server.code === selectedServer?.code ? "wireguard-server-option--active" : ""
                      }`}
                      onClick={() => setSelectedServerCode(server.code)}
                    >
                      <span>{server.name}</span>
                      {server.is_default && <small>Основной</small>}
                    </button>
                  ))}
                </div>

                <div className="wireguard-config-card__actions">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    loading={bundleLoading}
                    disabled={loading || bundleLoading}
                    onClick={downloadAll}
                  >
                    Скачать все профили (.zip)
                  </Button>
                </div>

                {bundleLoading && (
                  <div className="wireguard-bundle-progress" role="status" aria-live="polite">
                    <div className="wireguard-bundle-progress__header">
                      <span className="wireguard-bundle-progress__spinner" aria-hidden="true" />
                      <strong>Готовим недостающие профили</strong>
                      <span>{bundleReadyCount} из {servers.length}</span>
                    </div>
                    <Progress percent={bundlePercent} showInfo={false} strokeColor="#315fd6" />
                    <div className="wireguard-bundle-progress__servers">
                      {servers.map((server) => {
                        const status = bundleStatuses[server.code] || "preparing";
                        return (
                          <span key={server.code} className={`wireguard-bundle-progress__server wireguard-bundle-progress__server--${status}`}>
                            {status === "ready" ? "✓" : status === "failed" ? "!" : "•"} {server.name}
                          </span>
                        );
                      })}
                    </div>
                    <small>Можно подождать на этой странице — архив скачается автоматически.</small>
                  </div>
                )}

                {selectedServer && (
                  <div className="wireguard-selected-server">
                    <h4>{selectedServer.name}</h4>
                    {selectedQueueMessage && (
                      <Alert
                        type="info"
                        showIcon
                        message="Создание VPN-профиля"
                        description={selectedQueueMessage}
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    {selectedConfig ? (
                      <>
                        <div className="wireguard-qr-wrapper">
                          <QRCode value={selectedConfig} size={220} />
                        </div>
                        <p className="wireguard-config-card__hint">
                          Endpoint: {selectedServer.endpoint}
                        </p>
                        <div className="wireguard-config-card__actions">
                          <Button
                            type="primary"
                            loading={loading}
                            disabled={loading || bundleLoading}
                            onClick={() => download(selectedServer)}
                          >
                            <DownloadOutlined /> Скачать .conf
                          </Button>
                          <Popconfirm
                            title="Обновить профиль?"
                            description="Старый профиль сразу перестанет работать. Новый файл нужно импортировать заново."
                            okText="Обновить"
                            cancelText="Отмена"
                            onConfirm={() => refresh(selectedServer.code)}
                          >
                            <Button icon={<SyncOutlined />} loading={loading} disabled={loading || bundleLoading}>
                              Обновить профиль
                            </Button>
                          </Popconfirm>
                        </div>
                      </>
                    ) : (
                      <div className="wireguard-config-card__empty">
                        <p>Конфигурация для {selectedServer.name} ещё не создана.</p>
                        <div className="wireguard-config-card__actions">
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            className="wireguard-primary-cta"
                            loading={loading}
                            disabled={loading || bundleLoading}
                            onClick={() => generate(selectedServer.code)}
                          >
                            Получить профиль
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        <div className="wireguard-step-card">
          <p className="wireguard-step-card__index">2. Установите приложение</p>
          <p className="wireguard-step-card__subtitle">
            Выберите свою платформу: для Windows доступно прямое скачивание, для Android и Apple есть файл и ссылка на магазин.
          </p>
          <div className="wireguard-apps-title">
            <span>Приложения</span>
          </div>
          <div className="wireguard-app-grid">
            {apps
              .filter((app) => !platform || app.platform === platform)
              .map((app) => (
                <Button
                  key={app.key}
                  className="wireguard-app-card"
                  href={app.href}
                  target={app.download ? undefined : "_blank"}
                  rel="noreferrer"
                  download={app.download}
                  type="default"
                >
                  <img className="wireguard-app-icon" src={app.icon} alt={app.iconAlt} />
                  {app.label}
                </Button>
              ))}
          </div>
        </div>
      </section>

      <details className="wireguard-disclosure">
        <summary>Видеоинструкция WireGuard</summary>
        <div className="wireguard-video-frame">
          <video controls preload="metadata" playsInline className="wireguard-video-player">
            <source src="/WIREGUARD.mp4" type="video/mp4" />
          </video>
        </div>
      </details>

      <details className="wireguard-help">
        <summary>Инструкция по подключению</summary>
        <div className="wireguard-help__icon">i</div>
        <div>
          <div className="wireguard-help__grid">
            <div>
              <strong>Для iOS / macOS:</strong>
              <span>
                Установите WireGuard, нажмите «+», выберите «Create from QR Code»
                или импортируйте скачанный .conf файл.
              </span>
            </div>
            <div>
              <strong>Для Android / Windows:</strong>
              <span>
                Откройте WireGuard, выберите «Import tunnel(s) from file» или
                сканируйте QR-код, затем активируйте туннель переключателем.
              </span>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
};

export default WireGuardInfo;
